import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import type ComponentTypes from "~/features/cms/componentTypes";

export interface CMSPageNavigationElementProps {
  label: string;
  href: string;
}

export interface CMSPageNavigationComponentProps {
  className?: string;
  type: ComponentTypes.CMSPageNavigation;
  items: CMSPageNavigationElementProps[]; // not `elements`
}

export const CMSPageNavigation = ({ items, className }: CMSPageNavigationComponentProps) => {
  // const { activeIndex, triggerSetActiveIndex } = usePageNavigation(items);
  const navigationRef = useRef<HTMLDivElement>(null);
  const [navigationHeight, setNavigationHeight] = useState(0);
  const { activeIndex } = usePageNavigation(items, navigationHeight);

  useEffect(() => {
    const element = navigationRef.current;
    if (!element) {
      return;
    }

    const updateNavigationHeight = () => setNavigationHeight(element.getBoundingClientRect().height);
    updateNavigationHeight();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(updateNavigationHeight);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = navigationRef.current;
    if (el && el.parentElement) {
      el.parentElement.classList.add("scha__cmspagenav__parent-container");
      return () => el.parentElement?.classList.remove("scha__cmspagenav__parent-container");
    }
  }, [navigationRef.current]);

  useEffect(() => {
    if (!window.location.hash) {
      return;
    }

    const element = document.querySelector(window.location.hash);
    if (element && navigationRef.current) {
      const navHeight = navigationRef.current.getBoundingClientRect().height;
      const elementOffset = getElementPageOffsetTop(element as HTMLElement);
      window.scrollTo({ top: elementOffset - navHeight });
    }
  }, [navigationRef.current]);

  if (!items?.length) {
    return null;
  }

  const handleNavigationClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const element = document.querySelector(e.currentTarget.getAttribute("href") ?? "");
    if (element && navigationRef.current) {
      if (element.id) {
        window.location.hash = element.id;
      }
      const navHeight = navigationRef.current.getBoundingClientRect().height;
      const elementOffset = getElementPageOffsetTop(element as HTMLElement);
      window.scrollTo({ top: elementOffset - navHeight, behavior: "smooth" });
    }
  };

  return (
    <div className={classNames("scha__cmspagenav", className)} ref={navigationRef}>
      <ul className="scha__cmspagenav__elements">
        {items.map((element, index) => (
          <li
            key={`scha__cmspagenav-${index}`}
            id={`nav-${encodeURIComponent(element.href.replace("#", ""))}`}
            className={classNames("scha__cmspagenav__element", { active: index === activeIndex })}
          >
            <a
              href={element.href}
              className={classNames("scha__cmspagenav__link", { active: index === activeIndex })}
              // onClick={() => triggerSetActiveIndex(index)}
              onClick={handleNavigationClick}
            >
              <span className="scha__cmspagenav__label">{element.label}</span>
            </a>
          </li>
        ))}
      </ul>
      <hr className="scha__cmspagenav__line" />
    </div>
  );
};

const EPSILON_OFFSET = 2;

export function usePageNavigation(items: CMSPageNavigationElementProps[], viewportOffset = 0) {
  const [activeIndex, setActiveIndex] = useState(0);
  const itemHrefsKey = JSON.stringify(items.map(({ href }) => href));

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") {
      return;
    }

    const itemHrefs = JSON.parse(itemHrefsKey) as string[];
    if (itemHrefs.length === 0) {
      return;
    }

    const resolveSectionIndices = () => {
      const resolvedSections = new Map<Element, number>();
      itemHrefs.forEach((href, index) => {
        try {
          const section = document.querySelector(href);
          if (section) {
            resolvedSections.set(section, index);
          }
        } catch {
          // Invalid CMS anchors are ignored in the same way as missing sections.
        }
      });
      return resolvedSections;
    };

    let sectionIndices = resolveSectionIndices();
    let passedSectionIndices = new Set<number>();
    const sectionObservers = new Map<Element, IntersectionObserver>();
    let sectionResizeObserver: ResizeObserver | undefined;

    const publishActiveIndex = () => {
      const newActiveIndex = Math.max(0, ...passedSectionIndices);
      setActiveIndex((currentActiveIndex) =>
        currentActiveIndex === newActiveIndex ? currentActiveIndex : newActiveIndex,
      );
    };

    const observeSections = () => {
      sectionObservers.forEach((observer) => observer.disconnect());
      sectionObservers.clear();

      sectionIndices.forEach((index, section) => {
        const sectionHeight = Math.max(1, section.getBoundingClientRect().height);
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (!entry) {
              return;
            }

            if (!entry.isIntersecting && entry.boundingClientRect.top <= viewportOffset + EPSILON_OFFSET) {
              passedSectionIndices.add(index);
            } else {
              passedSectionIndices.delete(index);
            }
            publishActiveIndex();
          },
          {
            root: null,
            rootMargin: `-${viewportOffset + EPSILON_OFFSET + sectionHeight}px 0px 0px 0px`,
            threshold: 0,
          },
        );
        observer.observe(section);
        sectionObservers.set(section, observer);
      });
    };

    const reconcileSectionIdentities = () => {
      const resolvedSections = resolveSectionIndices();
      const identitiesChanged =
        resolvedSections.size !== sectionIndices.size ||
        [...resolvedSections].some(([section, index]) => sectionIndices.get(section) !== index);
      if (identitiesChanged) {
        const resolvedIndices = new Set(resolvedSections.values());
        passedSectionIndices = new Set([...passedSectionIndices].filter((index) => resolvedIndices.has(index)));
        sectionIndices = resolvedSections;
        sectionResizeObserver?.disconnect();
        sectionIndices.forEach((_index, section) => sectionResizeObserver?.observe(section));
        observeSections();
        publishActiveIndex();
      }
    };

    const mutationAffectsSections = (mutations: MutationRecord[]) =>
      mutations.some((mutation) => {
        if (mutation.type === "attributes") {
          const target = mutation.target as Element;
          if (sectionIndices.has(target)) {
            return true;
          }
          return itemHrefs.some((href) => {
            try {
              return target.matches(href);
            } catch {
              return false;
            }
          });
        }

        return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
          if (!(node instanceof Element)) {
            return false;
          }
          return itemHrefs.some((href) => {
            try {
              return node.matches(href) || Boolean(node.querySelector(href));
            } catch {
              return false;
            }
          });
        });
      });

    observeSections();
    window.addEventListener("resize", observeSections);
    window.visualViewport?.addEventListener("resize", observeSections);
    if (typeof ResizeObserver !== "undefined") {
      sectionResizeObserver = new ResizeObserver(observeSections);
      sectionIndices.forEach((_index, section) => sectionResizeObserver?.observe(section));
    }
    const mutationObserver = new MutationObserver((mutations) => {
      if (mutationAffectsSections(mutations)) {
        reconcileSectionIdentities();
      }
    });
    mutationObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ["id"],
      childList: true,
      subtree: true,
    });
    return () => {
      window.removeEventListener("resize", observeSections);
      window.visualViewport?.removeEventListener("resize", observeSections);
      mutationObserver.disconnect();
      sectionResizeObserver?.disconnect();
      sectionObservers.forEach((observer) => observer.disconnect());
    };
  }, [itemHrefsKey, viewportOffset]);

  return {
    activeIndex,
  };
}

function getElementPageOffsetTop(element: HTMLElement): number {
  let offsetTop = 0;
  let current: HTMLElement | null = element;

  while (current) {
    offsetTop += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }

  return offsetTop;
}
